/**
 * App Component
 *
 * Description: This component serves as the main entry point for the application, handling socket connections and fetching unread notifications.
 * It initializes the router and manages the application's global state through Redux.
 *
 * File Name: App.tsx
 * Date: 06:03 PM PKT, Thursday, June 19, 2025
 * Marker: 28-07-2024
 */

import React, { useEffect, useRef, useState } from "react";

// Import styles
import "./App.css";
import "./assets/css/style.css";
import "./assets/css/responsive.css";
import "react-quill/dist/quill.snow.css";
import "aos/dist/aos.css";

// Import the router
import Router from "./router";
import ToastProvider from "./Components/ToastProvider";
import { decodedToken, getToken } from "./helpers/helperFunctions";
import {
  initializeSocket,
  turnOffSocket,
  turnOnSocket,
} from "./Sockets/socket";
import { useDispatch } from "react-redux";
import httpRequest from "./helpers/httpRequest";
import { setAuth, setCounter, setUserDetails } from "src/Store/reducer";
import { getIndicators, removeAllIndicators } from "src/Store/redDots";
import { RootState, AppDispatch } from "@store/store";
import { useSelector } from "react-redux";
import AOS from "aos";
import { setCartItems } from "src/Store/Cart";
import { customer, doctor } from "./lib/constans";

// Extend the Window interface to include googleTranslateElementInit
declare global {
  interface Window {
    googleTranslateElementInit?: () => void;
    google?: any;
  }
}

const App: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const { userDetails, websiteLanguage } = useSelector(
    (store: RootState) => store.appReducer
  );
  const { cartItems } = useSelector((store: RootState) => store.cart);
  const [translateScriptLoaded, setTranslateScriptLoaded] = useState(false);
  const translateElementRef = useRef<HTMLDivElement>(null);

  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 1200,
      easing: "ease-out-cubic",
      once: false,
      offset: 50,
      delay: 0,
      mirror: true,
    });
  }, []);

  // Function to handle socket initialization
  const initializeSockets = async () => {
    try {
      await initializeSocket(dispatch);
      await turnOnSocket();
    } catch (error) {
      console.error("Socket initialization error:", error);
    }
  };

  // Function to handle socket disconnection
  const disconnectSockets = async () => {
    try {
      await turnOffSocket();
    } catch (error) {
      console.error("Socket disconnection error:", error);
    }
  };

  const fetchunReadCount = async () => {
    try {
      const { res, err } = await httpRequest<{
        messageUnreadCount: number;
        unreadNotificationCount: number;
      }>({ path: "/notification-setting/un-read/count" });
      if (res) {
        dispatch(
          setCounter({ key: "messageUnreadCount", val: res.messageUnreadCount })
        );
        dispatch(
          setCounter({
            key: "unreadNotificationCount",
            val: res.unreadNotificationCount,
          })
        );
      } else {
        console.log("fetchunReadCount error:", err);
      }
    } catch (error) {
      console.error("Unexpected error in fetchunReadCount:", error);
    }
  };

  const token = getToken();
  
  useEffect(() => {
    let usRole = decodedToken(token) ? decodedToken(token)?.userRole : "";
    if (token && (usRole === doctor || usRole === customer)) {
      (async () => {
        const { res } = await httpRequest<any>({ path: usRole === customer ? "/customer/profile" : `/doctors/profile/${decodedToken(token)?.id}` });
        if (res) {
          dispatch(setUserDetails(res));
          dispatch(setAuth(token));
          if (cartItems.length === 0 && usRole === customer) {
            const cartResponse : any = await httpRequest({
              path: "/cart/all",
              method: "get",
              token: token,
            });
            if (cartResponse.err) {
              return;
            } else {
              // Check if cartResponse.res is an array before spreading
              if (Array.isArray(cartResponse.res)) {
                dispatch(setCartItems([...cartResponse.res?.result]));
              } else if (cartResponse.res && typeof cartResponse.res === 'object') {
                // If it's an object with items property
                const items = cartResponse?.res?.result || [];
                dispatch(setCartItems(Array.isArray(items) ? items : []));
              } else {
                // If it's not an array or object, set empty array
                dispatch(setCartItems([]));
              }
            }
          }
        }
      })();
    }
  }, [token]);

  useEffect(() => {
    const manageSocketConnections = async () => {
      if (token) {
        fetchunReadCount();
        await disconnectSockets();
        await initializeSockets();
      } else {
        await turnOffSocket();
      }
    };

    manageSocketConnections();

    return () => {
      turnOffSocket();
    };
  }, [token]);

  useEffect(() => {
    if (!userDetails?.userRole) return;

    let interval: NodeJS.Timeout;
    const fetchIndicators = () => {
      if (
        userDetails.userRole === "superAdmin" ||
        userDetails.userRole === "operator"
      ) {
        dispatch(getIndicators("/operators/admin-indicators"));
      } else if (
        userDetails.userRole === "customer" ||
        userDetails.userRole === "doctor"
      ) {
        dispatch(getIndicators("/customer/user-indicators"));
      }
    };
    fetchIndicators();
    interval = setInterval(fetchIndicators, 30000);

    return () => clearInterval(interval);
  }, [dispatch, userDetails?.userRole]);

  // Global error handling
  useEffect(() => {
    const handleGlobalError = (event: ErrorEvent) => {
      console.error(
        "Uncaught error:",
        event.message,
        event.filename,
        event.lineno
      );
    };
    window.addEventListener("error", handleGlobalError);

    return () => window.removeEventListener("error", handleGlobalError);
  }, []);

  return (
    <ToastProvider>
      <div>
        <div
          id="google_translate_element"
          ref={translateElementRef}
          style={{ display: "none" }}
        ></div>
        <Router />
      </div>
    </ToastProvider>
  );
};

export default App;
